// Shared export utilities for the vSDI dashboard.
// Every tab passes a normalized "export spec" and gets consistent
// PDF / Excel / Outlook / HTML output that honours the active UI filters.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const NAVY_RGB = [26, 54, 102];
const TEAL_RGB = [0, 163, 163];

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

function appliedFilterSummary(filters) {
  const active = Object.entries(filters || {})
    .filter(([, v]) => v !== "" && v != null && v !== "all")
    .map(([k, v]) => `${k}: ${v}`);
  return active.length ? `Filters: ${active.join(" · ")}` : "Filters: none";
}

function todayStr() {
  return new Date().toLocaleString();
}

// ══════════════ PDF ═══════════════
export function exportToPDF({ title, subtitle, filters, columns, rows, filename }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  // Header band
  doc.setFillColor(...NAVY_RGB);
  doc.rect(0, 0, pageW, 56, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("valantic vSDI · Billability & Utilization", 32, 22);
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text(title, 32, 40);

  // Meta
  doc.setTextColor(60, 60, 60);
  doc.setFontSize(8);
  let metaY = 72;
  if (subtitle) { doc.text(subtitle, 32, metaY); metaY += 12; }
  doc.text(`Generated: ${todayStr()}`, 32, metaY); metaY += 10;
  doc.text(appliedFilterSummary(filters), 32, metaY); metaY += 10;
  doc.text(`${rows.length} rows`, 32, metaY); metaY += 6;

  autoTable(doc, {
    startY: metaY + 10,
    head: [columns.map((c) => c.label)],
    body: rows.map((r) => columns.map((c) => (typeof c.get === "function" ? c.get(r) : r[c.key] ?? ""))),
    theme: "striped",
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: TEAL_RGB, textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 32, right: 32 },
  });
  doc.save(filename || `vSDI_${title.replace(/\W+/g, "_")}.pdf`);
}

// ══════════════ EXCEL ═══════════════
export function exportToExcel({ title, filters, columns, rows, filename, sheetName = "Data" }) {
  const aoa = [];
  aoa.push([`valantic vSDI — ${title}`]);
  aoa.push([`Generated: ${todayStr()}`]);
  aoa.push([appliedFilterSummary(filters)]);
  aoa.push([`${rows.length} rows`]);
  aoa.push([]);
  aoa.push(columns.map((c) => c.label));
  rows.forEach((r) => {
    aoa.push(columns.map((c) => (typeof c.get === "function" ? c.get(r) : r[c.key] ?? "")));
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = columns.map((c) => ({ wch: Math.max(c.label.length + 2, c.width ?? 16) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const fallback = `vSDI_${title.replace(/\W+/g, "_")}.xlsx`;
  const finalName = filename
    ? (/\.xlsx$/i.test(filename) ? filename : `${filename.replace(/\.xls$/i, "")}.xlsx`)
    : fallback;
  XLSX.writeFile(wb, finalName);
}

// ══════════════ HTML ═══════════════
function buildHTML({ title, subtitle, filters, columns, rows }) {
  return `<!doctype html>
<html><head><meta charset="utf-8"><title>vSDI — ${esc(title)}</title>
<style>
  body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; margin: 0; background: #f1f5f9; color: #0f172a; }
  .hdr { background: linear-gradient(135deg,#0d1f3c 0%,#1a3666 100%); color: #fff; padding: 24px 32px; }
  .hdr h1 { margin: 0; font-size: 22px; } .hdr h2 { margin: 6px 0 0; font-size: 13px; font-weight: 400; opacity:.85; }
  .wrap { padding: 24px 32px; }
  .meta { font-size: 12px; color: #64748b; margin-bottom: 14px; }
  table { width: 100%; border-collapse: collapse; background:#fff; border-radius: 8px; overflow:hidden; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
  th, td { padding: 8px 12px; text-align: left; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
  th { background: #00a3a3; color: #fff; font-size: 11px; letter-spacing:.04em; text-transform: uppercase; }
  tr:nth-child(even) td { background: #f8fafc; }
  .badge { display:inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
  .badge-over { background:#fee2e2; color:#b91c1c; }
  .badge-under { background:#fef3c7; color:#b45309; }
  .badge-opt { background:#d1fae5; color:#047857; }
  .badge-idle { background:#e2e8f0; color:#475569; }
  .ftr { padding: 16px 32px; font-size: 11px; color:#94a3b8; text-align:center; }
</style></head>
<body>
  <div class="hdr">
    <h1>valantic vSDI · ${esc(title)}</h1>
    ${subtitle ? `<h2>${esc(subtitle)}</h2>` : ""}
  </div>
  <div class="wrap">
    <div class="meta">
      Generated: ${esc(todayStr())}<br>
      ${esc(appliedFilterSummary(filters))}<br>
      ${rows.length} rows
    </div>
    <table>
      <thead><tr>${columns.map((c) => `<th>${esc(c.label)}</th>`).join("")}</tr></thead>
      <tbody>
        ${rows.map((r) => `<tr>${columns.map((c) => {
          const v = typeof c.get === "function" ? c.get(r) : r[c.key] ?? "";
          if (c.key === "status" || c.isStatus) {
            const s = String(v).toLowerCase();
            const cls = s.includes("over") ? "badge-over" : s.includes("under") ? "badge-under" : s.includes("optimal") ? "badge-opt" : "badge-idle";
            return `<td><span class="badge ${cls}">${esc(v)}</span></td>`;
          }
          return `<td>${esc(v)}</td>`;
        }).join("")}</tr>`).join("")}
      </tbody>
    </table>
  </div>
  <div class="ftr">Auto-generated by the valantic vSDI Billability Dashboard</div>
</body></html>`;
}

export function openInHTML(spec) {
  const html = buildHTML(spec);
  const w = window.open("", "_blank", "width=1200,height=800");
  if (!w) { alert("Pop-up blocked — please allow pop-ups for this site."); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

// ══════════════ OUTLOOK ═══════════════
// Builds a text/markdown-like body suitable for Outlook body pasting.
// Also opens mailto: with pre-filled subject + body.
function buildOutlookBody({ title, subtitle, filters, columns, rows }) {
  const bar = "=".repeat(Math.max(title.length + 16, 68));
  const widths = columns.map((c) => Math.max(
    c.label.length,
    ...rows.map((r) => String(typeof c.get === "function" ? c.get(r) : r[c.key] ?? "").length)
  ));
  const fmt = (arr) => "| " + arr.map((v, i) => String(v ?? "").padEnd(widths[i])).join(" | ") + " |";
  const lines = [
    bar,
    `vSDI REPORT — ${title.toUpperCase()}`,
    bar,
    subtitle ? `Context       : ${subtitle}` : null,
    `Generated On  : ${todayStr()}`,
    appliedFilterSummary(filters),
    `Row count     : ${rows.length}`,
    "",
    fmt(columns.map((c) => c.label)),
    fmt(widths.map((w) => "-".repeat(w))),
    ...rows.map((r) => fmt(columns.map((c) => (typeof c.get === "function" ? c.get(r) : r[c.key] ?? "")))),
    "",
    "-- ",
    "Auto-generated by the valantic vSDI Billability Dashboard.",
  ].filter(Boolean);
  return lines.join("\n");
}

export function openInOutlook(spec, { subject, to = "" } = {}) {
  const body = buildOutlookBody(spec);
  const subj = encodeURIComponent(subject || `vSDI — ${spec.title}`);
  // mailto bodies have length limits (~2000 chars on Windows). Truncate with hint if needed.
  let txt = body;
  const MAX = 1900;
  if (txt.length > MAX) {
    txt = txt.slice(0, MAX) + `\n\n... (${body.length - MAX} more chars truncated — please use Excel/PDF export for the full dataset)`;
  }
  const url = `mailto:${encodeURIComponent(to)}?subject=${subj}&body=${encodeURIComponent(txt)}`;
  // Try to open in a new window first (avoids navigating away)
  const w = window.open(url, "_blank");
  if (!w) window.location.href = url;
}
